// Copyright 2020 Sorama B.V.

import { computedFrom, PLATFORM } from "aurelia-framework";
import { ColorPicker } from "controls/color-picker/ColorPicker";
import {
    CanvasMediaObjectLinkDTO,
    LinkedMediaObjectType,
    TextAlignments,
    TextBoxDTO,
    UpdateTextBoxDTO,
} from "datatypes/SoramaWebApi";
import { ObjectExtensions } from "extensions/ObjectExtensions";
import { ICanvasMediaObjectLinkRepository } from "repositories/CanvasMediaObjectLinkRepository";
import { ILinkedMediaObjectRepository } from "repositories/LinkedMediaObjectRepository";
import { Color } from "services/Color/Color";
import { BoundingBox, IComposableParent } from "./../ComposableCanvas";
import { ComposableContentType, ComposableMediaObject, IComposableMediaObjectAction } from "./ComposableMediaObject";

export class ComposableTextMediaObject extends ComposableMediaObject {
    public readonly template: string = PLATFORM.moduleName("controls/composable-canvas/templates/TextTemplate.html");
    public readonly preserveAspectRatio: boolean = false;
    public readonly contentType: ComposableContentType = ComposableContentType.fixedScale;
    private textBox: TextBoxDTO;

    // Update promise that ensures a single update at a time and that updates do no interleave causing weird effects.
    private contentUpdatePromise: Promise<void>;
    private updateTimeout: number;
    private pendingUpdate: UpdateTextBoxDTO = {};

    private textPicker: ColorPicker;
    private fillPicker: ColorPicker;

    public get mediaObjectType(): LinkedMediaObjectType {
        return LinkedMediaObjectType.TextBox;
    }

    @computedFrom("canvasSize", "pendingUpdate.fontSize", "textBox.fontSize")
    protected get fontSize(): number {
        // If there is a pending update, take that font size, otherwise take the one from the textbox object
        const fontSize =
            this.pendingUpdate && this.pendingUpdate.fontSize !== undefined
                ? this.pendingUpdate.fontSize
                : this.textBox.fontSize;
        return this.canvasSize ? (fontSize / 100) * this.canvasSize.height : fontSize;
    }

    @computedFrom("pendingUpdate.textAlignment", "textBox.textAlignment")
    protected get textAlignment(): TextAlignments {
        return this.pendingUpdate && this.pendingUpdate.textAlignment !== undefined
            ? this.pendingUpdate.textAlignment
            : this.textBox.textAlignment;
    }

    @computedFrom("pendingUpdate.text", "textBox.text")
    protected get text(): string {
        return this.pendingUpdate && this.pendingUpdate.text !== undefined
            ? this.pendingUpdate.text
            : this.textBox.text;
    }
    protected set text(value: string) {
        this.queueContentUpdate((mutation) => (mutation.text = value));
    }

    @computedFrom("pendingUpdate.color", "textBox.color")
    protected get textColor(): string {
        return (this.pendingUpdate && this.pendingUpdate.color !== undefined
            ? this.pendingUpdate.color
            : this.textBox.color) as unknown as string;
    }
    protected set textColor(color: string) {
        if (color == (this.textBox.color as unknown as string)) {
            // Prevents some bouncing
            return;
        }

        this.queueContentUpdate((mutation) => (mutation.color = color as unknown as number));
    }

    @computedFrom("textColor")
    protected get cssTextColor(): string {
        return Color.fromHex(this.textColor).css;
    }

    @computedFrom("pendingUpdate.background", "textBox.background")
    protected get fillColor(): string {
        return (this.pendingUpdate && this.pendingUpdate.background
            ? this.pendingUpdate.background
            : this.textBox.background) as unknown as string;
    }
    protected set fillColor(color: string) {
        if (color == <unknown>this.textBox.background) {
            // Prevents some bouncing
            return;
        }

        this.queueContentUpdate((mutation) => (mutation.background = color as unknown as number));
    }

    @computedFrom("fillColor")
    protected get cssFillColor(): string {
        return Color.fromHex(this.fillColor).css;
    }

    constructor(
        private readonly mediaObjectRepository: ILinkedMediaObjectRepository,
        linkRepository: ICanvasMediaObjectLinkRepository,
        parent: IComposableParent,
        link: CanvasMediaObjectLinkDTO,
        initialSize: BoundingBox,
        textBox: TextBoxDTO
    ) {
        super(linkRepository, parent, link, initialSize);
        this.textBox = textBox;
    }

    private updateFontSize(inc: number) {
        this.queueContentUpdate(
            (mutation) => (mutation.fontSize = (mutation.fontSize ? mutation.fontSize : this.textBox.fontSize) + inc)
        );
    }

    private updateAlign(align: TextAlignments) {
        this.queueContentUpdate((mutation) => (mutation.textAlignment = align));
    }

    private showColorPickerTextColor() {
        this.textPicker.openPicker();
    }

    private showColorPickerFillColor() {
        this.fillPicker.openPicker();
    }

    public readonly additionalActions: IComposableMediaObjectAction[] = [
        { icon: "fas fa-plus", action: () => this.updateFontSize(1) },
        { icon: "fas fa-minus", action: () => this.updateFontSize(-1) },
        { icon: "fas fa-align-left", action: () => this.updateAlign(TextAlignments.Left) },
        { icon: "fas fa-align-center", action: () => this.updateAlign(TextAlignments.Center) },
        { icon: "fas fa-align-right", action: () => this.updateAlign(TextAlignments.Right) },
        { icon: "fas fa-palette", action: () => this.showColorPickerTextColor() },
        //{ icon: "fas fa-fill", action: () => this.showColorPickerFillColor() }
    ];

    private queueContentUpdate(mutation: (update: UpdateTextBoxDTO) => void): Promise<void> {
        const promise = this.contentUpdatePromise ? this.contentUpdatePromise : Promise.resolve();

        // Apply mutation
        mutation(this.pendingUpdate);

        if (this.updateTimeout) {
            // Update is pending, no need to queue again
            return this.contentUpdatePromise;
        }

        // Create a new pending update
        const newUpdatePromise = new Promise<void>((resolve, reject) => {
            // Let a timer expire to avoid spam
            this.updateTimeout = window.setTimeout(() => {
                delete this.updateTimeout;

                const appliedUpdate = ObjectExtensions.copy(this.pendingUpdate);

                // Apply all mutations
                this.mediaObjectRepository
                    .updateTextBox(this.textBox.id, appliedUpdate)
                    .then(() => {
                        const newUpdate: UpdateTextBoxDTO = {};
                        let requeue = false;

                        // Apply the pending update object to our texbox entity
                        for (const key in appliedUpdate) {
                            if (appliedUpdate[key] === undefined) {
                                continue;
                            }

                            this.textBox[key] = appliedUpdate[key];

                            if (
                                this.pendingUpdate[key] !== undefined &&
                                appliedUpdate[key] !== this.pendingUpdate[key]
                            ) {
                                // Value has been updated again in the mean time, queue another update
                                newUpdate[key] = this.pendingUpdate[key];
                                requeue = true;
                            }
                        }

                        // Update pending update object
                        this.pendingUpdate = newUpdate;

                        if (requeue) {
                            this.queueContentUpdate(() => {})
                                .then(() => resolve())
                                .catch((e) => reject(e));
                        } else {
                            resolve();
                        }
                    })
                    .catch((e) => {
                        // Clear pending update object
                        this.pendingUpdate = {};

                        reject(e);
                    });
            }, 200);
        });

        const errorPromise = promise.catch(() => {}).then(() => newUpdatePromise);
        return (this.contentUpdatePromise = errorPromise);
    }
}
